import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { SmlButton } from '~/components/ui/Buttons/SmlButton';
import { SmlErrorState } from '~/components/ui/States/Error/ErrorState';
import { ApplicationError } from '~/lib/ApplicationError';

interface ErrorProps {
    error: ApplicationError | unknown;
}

const ErrorStatusCodes = [403, 404, 410, 500, 'unknown'] as const;

/**
 * Defines specific error types to differentiate between errors with the same status code.
 * This allows the UI to display appropriate error messages based on the specific error reason.
 *
 * Example: Multiple 403 Forbidden errors may occur, where "seb_required" specifically
 * indicates a corrupted Safe Exam Browser (SEB) configuration.
 */
const ErrorTypes = ['seb_required', 'unknown'] as const;

/**
 * Component for showing application errors
 */
export function Error({ error: errorFromProps }: ErrorProps) {
    const intl = useIntl();

    let error: ApplicationError;
    let errorStatus: typeof ErrorStatusCodes[number] = 'unknown';
    let errorType: typeof ErrorTypes[number] = 'unknown';
    let errorMessage: string | undefined;

    if (
        typeof errorFromProps === 'object' &&
        'message' in errorFromProps &&
        typeof errorFromProps.message === 'string'
    ) {
        errorMessage = errorFromProps.message;
    }

    if (!(errorFromProps instanceof ApplicationError)) {
        error = new ApplicationError({
            message: errorMessage,
            errorMeta: errorFromProps,
        });
    } else {
        error = errorFromProps;
    }

    if (error.isUrqlCombinedError()) {
        const statusCodes = error.errorMeta?.graphQLErrors.map<number>(
            (error) => error.extensions.status as number
        );

        // As there can be multiple errors present at the same time,
        // prefer the most severe one
        if (statusCodes?.includes(500)) {
            errorStatus = 500;
        } else if (statusCodes?.includes(403)) {
            errorStatus = 403;
        } else if (statusCodes?.includes(404)) {
            errorStatus = 404;
        }

        for (const err of error.errorMeta?.graphQLErrors || []) {
            if (err.extensions.type === 'seb_required') {
                errorType = 'seb_required';
                break;
            }
        }
    }

    if (error.isRouteError()) {
        errorStatus = error.errorMeta?.status === 404 ? 404 : 'unknown';
    }

    if (error.isDynamicImportError()) {
        errorStatus = 410;
    }

    switch (errorStatus) {
        case 403:
            if (errorType === 'seb_required') {
                error.message = 'app.error.403.sebRequired.title';
                error.description = 'app.error.403.sebRequired.description';
            } else {
                error.message = 'app.error.403.title';
                error.description =
                    error.description || 'app.error.403.description';
            }
            break;
        case 404:
            error.message = 'app.error.404.title';
            error.description =
                error.description || 'app.error.404.description';
            break;
        case 410:
            error.message = 'app.error.410.title';
            error.description =
                error.description || 'app.error.410.description';
            break;
        case 500:
        default:
            error.message = 'app.error.500.title';
            error.description =
                error.description || 'app.error.500.description';
            break;
    }

    return (
        <SmlErrorState
            title={intl.formatMessage({
                id: error.message,
                defaultMessage: error.message,
            })}
            description={intl.formatMessage({
                id: error.description,
                defaultMessage: error.description,
            })}
        >
            <SmlErrorState.Actions>
                {(errorStatus === 404 ||
                    (errorStatus === 403 && errorType !== 'seb_required')) && (
                    <>
                        <SmlButton variant="primary" href="/">
                            <FormattedMessage id="app.error.actions.backHome" />
                        </SmlButton>
                    </>
                )}

                {errorStatus === 500 ||
                    (errorStatus === 410 && (
                        <>
                            <SmlButton
                                variant="primary"
                                onClick={() => window.location.reload()}
                            >
                                <FormattedMessage id="app.error.actions.retry" />
                            </SmlButton>
                            <SmlButton variant="secondary" href="/">
                                <FormattedMessage id="app.error.actions.backHome" />
                            </SmlButton>
                        </>
                    ))}
            </SmlErrorState.Actions>
        </SmlErrorState>
    );
}

/**
 * @deprecated Use `throw new ApplicationError(messange, errorMeta)` instead.
 */
export interface CustomErrorProps<T = unknown> {
    title: MessageDescriptor;
    errorMeta: T;
}
